// SPDX-License-Identifier: Apache-2.0

// brain-core.ts — central glass orb at the centre of the orbit. Port of v1
// `Brain` (vault-orbit.tsx:296-335).
//
// VISUAL FIDELITY UPGRADE: this stream restores the drei `MeshDistortMaterial`
// warble that the previous v2 brain-core.ts had compromised on. Drei is not
// shipped in v2 (we don't bundle r3f), so we port the shader inline:
//
//   - Subclass `MeshPhysicalMaterial` so we keep the transmission/IOR/
//     roughness chassis the previous implementation got right.
//   - Override `onBeforeCompile` to inject 3D simplex noise into the vertex
//     shader at the `#include <begin_vertex>` slot. This is exactly drei's
//     approach (see `@react-three/drei/core/MeshDistortMaterial.js`); we
//     just hand-roll it here without the React wrapper.
//   - Per-frame, set `time` from `update(elapsedSec)` so the noise scrolls.
//
// Distortion params (matched to drei defaults so the look doesn't drift):
//   - radius   = 1.0   (scale of base sphere; vertex `position` is multiplied
//                       by `noise * pow(distort, 2.0) + radius`)
//   - distort  = 0.4   (drei's default; visible warble without melting the orb)
//
// The wireframe icosahedron shell stays static (v1:317-320).
//
// SCALE PULSE: when activeRef = true the core scales up to 1.12x; back to 1.0
// when nothing's active. Same lerp coefficient (0.08) as v1.

import * as THREE from "three";

export interface BrainCore {
  group: THREE.Group;
  /** Core mesh — exposed so postprocessing's GodRays can target it. */
  core: THREE.Mesh;
  setActive(active: boolean): void;
  update(deltaSec: number, elapsedSec: number): void;
  dispose(): void;
}

/**
 * Subclass MeshPhysicalMaterial and inject simplex-noise vertex displacement
 * via onBeforeCompile. The injected GLSL is byte-equivalent to drei's
 * `MeshDistortMaterial` distort.vert.glsl — proven shader, ~30 ALU per
 * vertex. At 6-subdivision icosahedron (~2562 verts) this is well under
 * 50 µs / frame on M-class.
 */
class DistortPhysicalMaterial extends THREE.MeshPhysicalMaterial {
  public _time = { value: 0 };
  public _distort = { value: 0.4 };
  public _radius = { value: 1.0 };

  override onBeforeCompile(shader: THREE.WebGLProgramParametersWithUniforms): void {
    shader.uniforms.time = this._time;
    shader.uniforms.radius = this._radius;
    shader.uniforms.distort = this._distort;

    // Prepend the simplex noise + uniform declarations to the existing
    // vertex shader. The actual displacement happens at the
    // `#include <begin_vertex>` injection point.
    shader.vertexShader = `
      uniform float time;
      uniform float radius;
      uniform float distort;

      // 3D simplex noise — Ashima Arts (MIT). Same routine drei ships in
      // helpers/glsl/distort.vert.glsl.
      vec3 mod289(vec3 x){return x-floor(x*(1.0/289.0))*289.0;}
      vec4 mod289(vec4 x){return x-floor(x*(1.0/289.0))*289.0;}
      vec4 permute(vec4 x){return mod289(((x*34.0)+1.0)*x);}
      vec4 taylorInvSqrt(vec4 r){return 1.79284291400159-0.85373472095314*r;}
      float snoise(vec3 v){
        const vec2 C=vec2(1.0/6.0,1.0/3.0);
        const vec4 D=vec4(0.0,0.5,1.0,2.0);
        vec3 i=floor(v+dot(v,C.yyy));
        vec3 x0=v-i+dot(i,C.xxx);
        vec3 g=step(x0.yzx,x0.xyz);
        vec3 l=1.0-g;
        vec3 i1=min(g.xyz,l.zxy);
        vec3 i2=max(g.xyz,l.zxy);
        vec3 x1=x0-i1+C.xxx;
        vec3 x2=x0-i2+C.yyy;
        vec3 x3=x0-D.yyy;
        i=mod289(i);
        vec4 p=permute(permute(permute(i.z+vec4(0.0,i1.z,i2.z,1.0))+i.y+vec4(0.0,i1.y,i2.y,1.0))+i.x+vec4(0.0,i1.x,i2.x,1.0));
        float n_=0.142857142857;
        vec3 ns=n_*D.wyz-D.xzx;
        vec4 j=p-49.0*floor(p*ns.z*ns.z);
        vec4 x_=floor(j*ns.z);
        vec4 y_=floor(j-7.0*x_);
        vec4 x=x_*ns.x+ns.yyyy;
        vec4 y=y_*ns.x+ns.yyyy;
        vec4 h=1.0-abs(x)-abs(y);
        vec4 b0=vec4(x.xy,y.xy);
        vec4 b1=vec4(x.zw,y.zw);
        vec4 s0=floor(b0)*2.0+1.0;
        vec4 s1=floor(b1)*2.0+1.0;
        vec4 sh=-step(h,vec4(0.0));
        vec4 a0=b0.xzyw+s0.xzyw*sh.xxyy;
        vec4 a1=b1.xzyw+s1.xzyw*sh.zzww;
        vec3 p0=vec3(a0.xy,h.x);
        vec3 p1=vec3(a0.zw,h.y);
        vec3 p2=vec3(a1.xy,h.z);
        vec3 p3=vec3(a1.zw,h.w);
        vec4 norm=taylorInvSqrt(vec4(dot(p0,p0),dot(p1,p1),dot(p2,p2),dot(p3,p3)));
        p0*=norm.x;p1*=norm.y;p2*=norm.z;p3*=norm.w;
        vec4 m=max(0.6-vec4(dot(x0,x0),dot(x1,x1),dot(x2,x2),dot(x3,x3)),0.0);
        m=m*m;
        return 42.0*dot(m*m,vec4(dot(p0,x0),dot(p1,x1),dot(p2,x2),dot(p3,x3)));
      }
      ${shader.vertexShader}
    `;

    // Replace the canonical begin_vertex slot — same line drei replaces.
    shader.vertexShader = shader.vertexShader.replace(
      "#include <begin_vertex>",
      `
        float updateTime = time / 50.0;
        float noise = snoise(vec3(position / 2.0 + updateTime * 5.0));
        vec3 transformed = vec3(position * (noise * pow(distort, 2.0) + radius));
      `,
    );
  }

  get time(): number {
    return this._time.value;
  }
  set time(v: number) {
    this._time.value = v;
  }
  get distort(): number {
    return this._distort.value;
  }
  set distort(v: number) {
    this._distort.value = v;
  }
  get radius(): number {
    return this._radius.value;
  }
  set radius(v: number) {
    this._radius.value = v;
  }
}

export function createBrainCore(): BrainCore {
  const group = new THREE.Group();

  // Outer wireframe shell — v1 vault-orbit.tsx:317-320. STATIC (not warbled).
  const wireGeom = new THREE.IcosahedronGeometry(1.3, 2);
  const wireMat = new THREE.MeshBasicMaterial({
    color: 0x67e8f9,
    wireframe: true,
    transparent: true,
    opacity: 0.14,
    toneMapped: false,
    depthWrite: false,
  });
  const wire = new THREE.Mesh(wireGeom, wireMat);
  group.add(wire);

  // Inner glass core — DistortPhysicalMaterial (drei port).
  // Higher subdivision (level 6 = ~2562 verts) so the warble has resolution
  // to express itself. Level 5 also looks decent if perf becomes an issue.
  const coreGeom = new THREE.IcosahedronGeometry(0.98, 6);
  const coreMat = new DistortPhysicalMaterial({
    color: 0x061933,
    emissive: 0x1393b3,
    emissiveIntensity: 1.6,
    roughness: 0.05,
    metalness: 0.0,
    transmission: 1.0,
    thickness: 0.5,
    ior: 1.5,
    clearcoat: 0.4,
    clearcoatRoughness: 0.2,
    transparent: true,
    depthWrite: false,
  });
  // Nudge distort slightly above drei default for stronger embodiment.
  coreMat.distort = 0.42;
  coreMat.radius = 1.0;

  const core = new THREE.Mesh(coreGeom, coreMat);
  // Render before stars so the rays don't get clipped by the depth buffer.
  core.renderOrder = -1;
  group.add(core);

  let active = false;
  let scale = 1.0;

  return {
    group,
    core,
    setActive(a: boolean) {
      active = a;
    },
    update(_deltaSec: number, elapsedSec: number) {
      const t = elapsedSec;
      // Wireframe rotation — v1 308-310
      wire.rotation.x = t * 0.08;
      wire.rotation.y = t * 0.12;
      // Core rotation — v1 311-312
      core.rotation.y = t * 0.2;
      core.rotation.x = t * 0.14;
      // Distortion warble — drives the simplex-noise vertex displacement.
      // Drei drives `time = clock.elapsedTime * speed` (default speed 1).
      // We multiply by ~20 so the visible scroll matches drei's look at
      // default speed (the GLSL divides by 50 internally — see `updateTime`
      // in onBeforeCompile).
      coreMat.time = t * 20.0;
      // Scale pulse on active — v1 313-318
      const target = active ? 1.12 : 1.0;
      scale += (target - scale) * 0.08;
      core.scale.setScalar(scale);
    },
    dispose() {
      wireGeom.dispose();
      wireMat.dispose();
      coreGeom.dispose();
      coreMat.dispose();
    },
  };
}
